import { Link } from '@inertiajs/react'
import { useState } from 'react'
import AppLayout from '../../components/AppLayout'
import { scoreColor, fmtScore } from '../../components/BreakdownScoreBar'
import { useTheme } from '../../contexts/ThemeContext'

const RARITY_LEVELS = [
  { key: -2, label: '定番',   bg: '#3a5a3a', text: '#fff' },
  { key: -1, label: '準定番', bg: '#5b7d4f', text: '#fff' },
  { key:  0, label: '標準',   bg: '#c8ccc4', text: '#444' },
  { key:  1, label: '準珍',   bg: '#c9963f', text: '#fff' },
  { key:  2, label: '珍しい', bg: '#c0463a', text: '#fff' },
]
const VEGTYPE_META = {
  T: { label: 'キホン',   bg: '#e0e0e0', text: '#555' },
  S: { label: '目玉',     bg: '#e07b39', text: '#fff' },
  C: { label: '変わり種', bg: '#7058a3', text: '#fff' },
  A: { label: 'おまけ',   bg: '#aaa',    text: '#fff' },
}
const SEASON_META = {
  '推し旬': { bg: '#c9963f', text: '#fff', icon: '★' },
  '旬':     { bg: '#4a7c59', text: '#fff', icon: '◎' },
  '避':     { bg: '#c0463a', text: '#fff', icon: '✕' },
  '通常':   { bg: '#e4e4e4', text: '#aaa', icon: '' },
}
const COLOR_HASH = {
  赤: '#e60033', 黒: '#2b2b2b', 紫: '#65318e', 白: '#f8fbf8', 橙: '#f08300',
  緑: '#3eb370', 薄: '#69b076', 深: '#00552e', 黄: '#ffec47', 茶: '#783c1d',
  桃: '#f09199', 桜: '#fef4f4', 茜: '#b7282e', 肌: '#fce2c4', 若: '#98d98e',
  浅: '#88cb7f', 青: '#00a497', 紺: '#223a70', 菫: '#7058a3', 紅: '#d7003a',
  苗: '#b0ca71', 柳: '#a8c97f', 錆: '#6c3524', 栗: '#762f07', 褐: '#8a3b00',
  金: '#e6b422',
}
function colorHex(name) { return name ? (COLOR_HASH[name] || null) : null }

// ── 旬グリッド（表示専用）────────────────────────────────────────────────
function SeasonGridDisplay({ seasonByMonth, C }) {
  const months = [1,2,3,4,5,6,7,8,9,10,11,12]
  const currentMonth = new Date().getMonth() + 1
  return (
    <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
      {months.map(m => {
        const status = seasonByMonth?.[m]
        const meta = status ? SEASON_META[status] : null
        const isCurrent = m === currentMonth
        const groupGap = (m === 4 || m === 7 || m === 10) ? '6px' : '2px'
        return (
          <div key={m} style={{ marginLeft: m === 1 ? 0 : groupGap, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <div style={{ fontSize: '8px', color: isCurrent ? C.ink : C.inkMuted, fontWeight: isCurrent ? 700 : 400, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{m}</div>
            <div title={status || '未登録'} style={{
              width: '20px', height: '26px', borderRadius: '3px',
              background: meta ? meta.bg : C.lineSoft,
              border: isCurrent ? `2px solid ${C.ink}` : `1px solid ${C.lineSoft}`,
              display: 'grid', placeItems: 'center',
              fontSize: '9px', color: meta ? meta.text : 'transparent', fontWeight: 700,
            }}>{meta?.icon || ''}</div>
            <div style={{ fontSize: '7px', color: C.inkMuted, lineHeight: 1, textAlign: 'center', maxWidth: '20px', overflow: 'hidden' }}>
              {status ? status.slice(0, 1) : ''}
            </div>
          </div>
        )
      })}
      <div style={{ marginLeft: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {Object.entries(SEASON_META).map(([s, m]) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: C.inkMuted }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: m.bg, display: 'inline-block', flexShrink: 0 }} />
            {s}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── ポジショニングカード ──────────────────────────────────────────────────
function PositioningCard({ positioning: p, C }) {
  if (!p) return null
  const deltaColor = (delta) => {
    if (delta == null) return C.inkMuted
    if (delta > 0.01) return C.greenDark
    if (delta < -0.01) return C.tomato
    return C.inkMuted
  }
  const fmtDelta = (v) => {
    if (v == null) return '—'
    const s = (v * 100).toFixed(1)
    return v > 0 ? `+${s}` : s
  }
  const rankBadge = (rank, total) => {
    if (!rank || !total) return '—'
    const pct = rank / total
    const color = pct <= 0.1 ? C.greenDark : pct <= 0.3 ? C.gold : pct >= 0.8 ? C.tomato : C.inkSoft
    return (
      <span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '18px', fontWeight: 700, color }}>{rank}</span>
        <span style={{ fontSize: '11px', color: C.inkMuted }}> / {total}品目中</span>
      </span>
    )
  }

  const deliveryDelta = p.item_avg_delivery != null && p.global_avg_delivery != null
    ? p.item_avg_delivery - p.global_avg_delivery : null
  const appealDelta = p.item_avg_appeal != null && p.global_avg_appeal != null
    ? p.item_avg_appeal - p.global_avg_appeal : null
  const countDelta = p.item_count != null && p.global_avg_count != null
    ? p.item_count - p.global_avg_count : null

  const metrics = [
    {
      label: 'お届けスコア 順位',
      rank: p.delivery_rank,
      score: p.item_avg_delivery,
      globalAvg: p.global_avg_delivery,
      delta: deliveryDelta,
    },
    {
      label: '魅力スコア 順位',
      rank: p.appeal_rank,
      score: p.item_avg_appeal,
      globalAvg: p.global_avg_appeal,
      delta: appealDelta,
    },
    {
      label: '使用回数 順位',
      rank: p.count_rank,
      score: p.item_count,
      globalAvg: p.global_avg_count,
      delta: countDelta,
      isCount: true,
    },
  ]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
      {metrics.map(m => (
        <div key={m.label} style={{ background: C.paper2, borderRadius: '8px', padding: '14px 16px', border: `1px solid ${C.lineSoft}` }}>
          <div style={{ fontSize: '10px', color: C.inkMuted, marginBottom: '6px', letterSpacing: '0.05em' }}>{m.label}</div>
          <div style={{ marginBottom: '8px' }}>{rankBadge(m.rank, p.total_items)}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
            <span style={{ color: C.inkMuted }}>
              {m.isCount ? `${m.score ?? '—'} 回` : m.score != null ? `${Math.round(m.score * 100)}pt` : '—'}
            </span>
            {m.delta != null && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: deltaColor(m.delta) }}>
                {m.isCount
                  ? `${m.delta > 0 ? '+' : ''}${m.delta.toFixed(1)}`
                  : fmtDelta(m.delta)}
                <span style={{ fontWeight: 400, fontSize: '10px', color: C.inkMuted }}> vs平均</span>
              </span>
            )}
          </div>
          {m.globalAvg != null && (
            <div style={{ fontSize: '9px', color: C.inkMuted, marginTop: '2px' }}>
              全体平均: {m.isCount ? `${m.globalAvg.toFixed(1)} 回` : `${Math.round(m.globalAvg * 100)}pt`}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 月次使用トレンド（SVG 棒グラフ）────────────────────────────────────
function MonthlyTrendChart({ trend, C }) {
  if (!trend || trend.length === 0) return <div style={{ color: C.inkMuted, padding: '24px', textAlign: 'center' }}>データなし</div>
  const W = 700, H = 120, PAD = { t: 10, r: 10, b: 40, l: 36 }
  const chartW = W - PAD.l - PAD.r
  const chartH = H - PAD.t - PAD.b
  const maxCnt = Math.max(...trend.map(r => r.count), 1)
  const barW = Math.max(8, Math.floor(chartW / trend.length) - 3)

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible', maxWidth: W }}>
      {[0, 0.5, 1].map(t => {
        const y = PAD.t + chartH * (1 - t)
        return <line key={t} x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke={C.lineSoft} strokeWidth={1} />
      })}
      {trend.map((r, i) => {
        const x = PAD.l + (i / trend.length) * chartW + (chartW / trend.length - barW) / 2
        const barH = (r.count / maxCnt) * chartH
        const y = PAD.t + chartH - barH
        const dlvColor = r.avg_delivery ? scoreColor(r.avg_delivery, C) : C.lineSoft
        return (
          <g key={`${r.year}-${r.month}`}>
            <rect x={x} y={y} width={barW} height={barH} fill={dlvColor} rx={2} opacity={0.85}
              title={`${r.year}/${r.month}: ${r.count}回 スコア${r.avg_delivery ? Math.round(r.avg_delivery * 100) : '—'}`}
            />
            <text x={x + barW / 2} y={H - PAD.b + 14} textAnchor="middle" fontSize={9} fill={C.inkMuted}>
              {r.month}月
            </text>
            {r.count > 0 && (
              <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize={9} fill={C.inkMuted}>
                {r.count}
              </text>
            )}
          </g>
        )
      })}
      <text x={PAD.l - 4} y={PAD.t + 4} textAnchor="end" fontSize={8} fill={C.inkMuted}>{maxCnt}</text>
      <text x={PAD.l - 4} y={PAD.t + chartH} textAnchor="end" fontSize={8} fill={C.inkMuted}>0</text>
    </svg>
  )
}

// ── 登場周期チャート（案I）──────────────────────────────────────────────
function RhythmChart({ rhythm, C }) {
  const { intervals, planning_frequency, summary } = rhythm
  if (!intervals || intervals.length < 2) return <div style={{ color: C.inkMuted, padding: '12px' }}>出現回数が少なく計算できません</div>

  const W = 700, H = 120, PAD = { t: 16, r: 10, b: 36, l: 40 }
  const chartW = W - PAD.l - PAD.r
  const chartH = H - PAD.t - PAD.b
  const data = intervals.filter(r => r.interval != null)
  const maxInt = Math.max(...data.map(r => r.interval), planning_frequency || 0, 1)
  const scaleY = v => PAD.t + chartH * (1 - v / maxInt)

  const pts = data.map((r, i) => [ PAD.l + (i / (data.length - 1 || 1)) * chartW, scaleY(r.interval) ])
  const polyline = pts.map(p => p.join(',')).join(' ')
  const idealY = planning_frequency ? scaleY(planning_frequency) : null

  const vsIdeal = summary.vs_ideal
  const rhythmColor = vsIdeal == null ? C.inkMuted
    : Math.abs(vsIdeal) <= 1 ? C.greenDark
    : vsIdeal > 0 ? C.tomato : C.gold

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible', maxWidth: W }}>
        {idealY && (
          <>
            <line x1={PAD.l} x2={W - PAD.r} y1={idealY} y2={idealY}
              stroke={C.inkMuted} strokeWidth={1} strokeDasharray="4 3" opacity={0.6} />
            <text x={W - PAD.r + 2} y={idealY + 4} fontSize={9} fill={C.inkMuted}>
              理想{planning_frequency}週
            </text>
          </>
        )}
        <polyline points={polyline} fill="none" stroke={C.greenDark} strokeWidth={2} />
        {pts.map(([ x, y ], i) => (
          <circle key={i} cx={x} cy={y} r={3} fill={C.greenDark}
            title={`${data[i].week}: ${data[i].interval}週間隔`} />
        ))}
        {[0, maxInt].map(v => (
          <text key={v} x={PAD.l - 4} y={scaleY(v) + 4} textAnchor="end" fontSize={8} fill={C.inkMuted}>{v}週</text>
        ))}
        {data.length > 0 && (
          <>
            <text x={pts[0][0]} y={H - PAD.b + 13} textAnchor="middle" fontSize={8} fill={C.inkMuted}>{data[0].week?.slice(0, 7)}</text>
            <text x={pts[pts.length - 1][0]} y={H - PAD.b + 13} textAnchor="middle" fontSize={8} fill={C.inkMuted}>{data[data.length - 1].week?.slice(0, 7)}</text>
          </>
        )}
      </svg>
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '10px' }}>
        {[
          { label: '平均間隔', value: `${summary.avg_interval} 週` },
          { label: '最短',     value: `${summary.min_interval} 週` },
          { label: '最長',     value: `${summary.max_interval} 週` },
          { label: '理想との差', value: vsIdeal != null ? `${vsIdeal > 0 ? '+' : ''}${vsIdeal} 週` : '—', color: rhythmColor },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: C.inkMuted, marginBottom: '2px' }}>{s.label}</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '14px', fontWeight: 600, color: s.color || C.ink }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── 評価軸スコアバー ─────────────────────────────────────────────────────
function CriterionBars({ scores, C }) {
  if (!scores || scores.length === 0) return <div style={{ color: C.inkMuted }}>データなし</div>
  const grouped = scores.reduce((acc, s) => {
    acc[s.category] = acc[s.category] || []
    acc[s.category].push(s)
    return acc
  }, {})

  const pctOf = v => v != null ? Math.max(0, Math.min(100, v * 100)) : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* 凡例 */}
      <div style={{ display: 'flex', gap: '14px', fontSize: '9px', color: C.inkMuted, alignItems: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '10px', height: '6px', background: C.greenDark, borderRadius: '3px' }} />本品目
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '2px', height: '12px', background: C.ink }} />平均
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '8px', height: '8px', border: `1px solid ${C.inkMuted}`, borderRadius: '50%' }} />最低 / 最高
        </span>
      </div>
      {Object.entries(grouped).map(([ cat, items ]) => (
        <div key={cat}>
          <div style={{ fontSize: '10px', letterSpacing: '0.1em', color: C.inkMuted, textTransform: 'uppercase', marginBottom: '6px' }}>{cat}</div>
          {items.map(s => {
            const pct = pctOf(s.avg_score)
            const avgPct = pctOf(s.global_avg)
            const maxPct = pctOf(s.global_max)
            const minPct = pctOf(s.global_min)
            const color = s.avg_score != null ? scoreColor(s.avg_score, C) : C.lineSoft
            const tooltip = [
              s.avg_score != null ? `本品目: ${Math.round(s.avg_score * 100)}` : null,
              s.global_avg != null ? `全体平均: ${Math.round(s.global_avg * 100)}` : null,
              s.global_min != null ? `最低: ${Math.round(s.global_min * 100)}` : null,
              s.global_max != null ? `最高: ${Math.round(s.global_max * 100)}` : null,
            ].filter(Boolean).join(' / ')
            return (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: C.inkSoft, width: '120px', flexShrink: 0 }}>{s.name}</span>
                <div title={tooltip} style={{ flex: 1, height: '14px', position: 'relative' }}>
                  {/* 背景トラック */}
                  <div style={{ position: 'absolute', inset: '4px 0', background: C.lineSoft, borderRadius: '3px' }} />
                  {/* 本品目バー */}
                  {pct != null && (
                    <div style={{
                      position: 'absolute', left: 0, top: '4px', height: '6px',
                      width: `${pct}%`, background: color, borderRadius: '3px',
                      transition: 'width 0.3s'
                    }} />
                  )}
                  {/* 最低マーカー */}
                  {minPct != null && (
                    <div title={`最低 ${Math.round(s.global_min * 100)}`} style={{
                      position: 'absolute', left: `calc(${minPct}% - 4px)`, top: '3px',
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: C.paper, border: `1.5px solid ${C.inkMuted}`,
                    }} />
                  )}
                  {/* 最高マーカー */}
                  {maxPct != null && (
                    <div title={`最高 ${Math.round(s.global_max * 100)}`} style={{
                      position: 'absolute', left: `calc(${maxPct}% - 4px)`, top: '3px',
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: C.paper, border: `1.5px solid ${C.inkMuted}`,
                    }} />
                  )}
                  {/* 平均ライン */}
                  {avgPct != null && (
                    <div title={`平均 ${Math.round(s.global_avg * 100)}`} style={{
                      position: 'absolute', left: `${avgPct}%`, top: 0,
                      width: '2px', height: '14px', background: C.ink, transform: 'translateX(-1px)',
                    }} />
                  )}
                </div>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color, width: '28px', textAlign: 'right' }}>{pct != null ? Math.round(pct) : '—'}</span>
                <span style={{ fontSize: '10px', color: C.inkMuted, width: '36px' }}>{s.count}回</span>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// ── 共起品目テーブル ─────────────────────────────────────────────────────
function CooccurrenceList({ items, C }) {
  const [sort, setSort] = useState('count')
  if (!items || items.length === 0) return <div style={{ color: C.inkMuted, textAlign: 'center', padding: '16px' }}>データなし</div>

  const sorted = [...items].sort((a, b) => {
    if (sort === 'count')    return b.count - a.count
    if (sort === 'delivery') return (b.avg_delivery || 0) - (a.avg_delivery || 0)
    if (sort === 'appeal')   return (b.avg_appeal   || 0) - (a.avg_appeal   || 0)
    return 0
  })
  const maxCnt = Math.max(...items.map(r => r.count), 1)

  const thStyle = (key, align = 'left') => ({
    padding: '7px 8px', textAlign: align, fontSize: '10px', color: C.inkMuted,
    fontWeight: 500, letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none',
    background: sort === key ? C.paper2 : 'transparent',
    borderBottom: `1px solid ${C.line}`,
  })
  const scoreCell = (v) => v != null ? (
    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: scoreColor(v, C), fontWeight: 600 }}>
      {Math.round(v * 100)}
    </span>
  ) : <span style={{ color: C.inkMuted, fontSize: '11px' }}>—</span>

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      <thead>
        <tr>
          <th style={{ ...thStyle('rank', 'center'), width: '24px' }}>#</th>
          <th style={thStyle('name')}>品目</th>
          <th style={{ ...thStyle('count', 'center') }} onClick={() => setSort('count')}>
            回数 {sort === 'count' && '▾'}
          </th>
          <th style={{ ...thStyle('delivery', 'center'), width: '44px' }} onClick={() => setSort('delivery')}>
            お届け {sort === 'delivery' && '▾'}
          </th>
          <th style={{ ...thStyle('appeal', 'center'), width: '44px' }} onClick={() => setSort('appeal')}>
            魅力 {sort === 'appeal' && '▾'}
          </th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r, i) => (
          <tr key={r.item_code} style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
            <td style={{ padding: '7px 8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: C.inkMuted }}>
              {String(i + 1).padStart(2, '0')}
            </td>
            <td style={{ padding: '7px 8px' }}>
              <Link href={`/item_analysis/${r.item_code}`} style={{ color: C.ink, textDecoration: 'none', fontSize: '12px' }}>
                {r.name}
              </Link>
            </td>
            <td style={{ padding: '7px 8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ flex: 1, height: '6px', background: C.lineSoft, borderRadius: '3px', overflow: 'hidden', minWidth: '40px' }}>
                  <div style={{ height: '100%', width: `${(r.count / maxCnt) * 100}%`, background: C.greenDark, borderRadius: '3px' }} />
                </div>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, width: '20px', textAlign: 'right' }}>
                  {r.count}
                </span>
              </div>
            </td>
            <td style={{ padding: '7px 8px', textAlign: 'center' }}>{scoreCell(r.avg_delivery)}</td>
            <td style={{ padding: '7px 8px', textAlign: 'center' }}>{scoreCell(r.avg_appeal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── セット一覧 ──────────────────────────────────────────────────────────
function SetsList({ sets, C }) {
  if (!sets || sets.length === 0) return <div style={{ color: C.inkMuted, textAlign: 'center', padding: '16px' }}>データなし</div>
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: C.paper2, borderBottom: `1px solid ${C.line}` }}>
            {['週', 'セット種別', 'プランコード', 'お届け', '魅力'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '10.5px', color: C.inkMuted, fontWeight: 500, letterSpacing: '0.08em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sets.map((r, i) => (
            <tr key={`${r.wer_id}-${i}`} style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
              <td style={{ padding: '8px 12px' }}>
                <Link href={`/weekly_evaluations/${r.week}`} style={{ color: C.ink, textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace" }}>
                  {r.week?.toString().slice(0, 10)}
                </Link>
              </td>
              <td style={{ padding: '8px 12px', color: C.inkSoft }}>{r.set_type_name || '—'}</td>
              <td style={{ padding: '8px 12px' }}>
                {r.set_plan_id ? (
                  <Link href={`/weekly_evaluations/${r.week}/set_plans/${r.set_plan_id}`}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted, textDecoration: 'none' }}>
                    {r.plan_code}
                  </Link>
                ) : <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted }}>{r.plan_code}</span>}
              </td>
              <td style={{ padding: '8px 12px' }}>
                {r.delivery_score != null ? (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: scoreColor(r.delivery_score, C) }}>
                    {fmtScore(r.delivery_score)}
                  </span>
                ) : '—'}
              </td>
              <td style={{ padding: '8px 12px' }}>
                {r.appeal_score != null ? (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: scoreColor(r.appeal_score, C) }}>
                    {fmtScore(r.appeal_score)}
                  </span>
                ) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── メイン ──────────────────────────────────────────────────────────────
export default function ItemAnalysisItemShow({
  item, season_by_month, positioning,
  monthly_trend, appearance_rhythm, criterion_scores, cooccurring_items, recent_sets
}) {
  const { theme: C } = useTheme()

  const rarity = RARITY_LEVELS.find(r => r.key === item?.rarity_score)
  const vegtype = item?.vegtype_flag ? VEGTYPE_META[item.vegtype_flag] : null

  const card = (title, content) => (
    <div style={{ background: C.paper, borderRadius: '10px', border: `1px solid ${C.line}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.lineSoft}`, fontWeight: 600, fontSize: '13px', color: C.ink }}>
        {title}
      </div>
      <div style={{ padding: '16px 18px' }}>{content}</div>
    </div>
  )

  return (
    <AppLayout bgColor={C.bg} maxWidth="1200px">
      {/* パンくず */}
      <div style={{ fontSize: '12px', color: C.inkMuted, marginBottom: '16px' }}>
        <Link href="/item_analysis" style={{ color: C.inkMuted, textDecoration: 'none' }}>品目分析</Link>
        <span style={{ margin: '0 6px' }}>›</span>
        <span>{item?.name}</span>
      </div>

      {/* ヘッダ */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: C.ink, margin: 0, fontFamily: "'Noto Serif JP', serif" }}>
            {item?.name}
          </h1>
          {rarity && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '3px', background: rarity.bg, color: rarity.text, fontWeight: 500 }}>{rarity.label}</span>}
          {vegtype && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '3px', background: vegtype.bg, color: vegtype.text, fontWeight: 500 }}>{vegtype.label}</span>}
          <span style={{ fontSize: '12px', color: C.inkMuted }}>{item?.middle_category_code}</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted }}>{item?.item_code}</span>
        </div>

        {/* 基本属性バー */}
        <div style={{ display: 'flex', gap: '0', marginTop: '14px', background: C.paper, border: `1px solid ${C.line}`, borderRadius: '8px', overflow: 'hidden' }}>
          {[
            { label: '理想頻度', value: item?.planning_frequency ? `1/${item.planning_frequency}週` : '—' },
            { label: 'ボリューム', value: item?.volume || '—' },
            {
              label: '色',
              value: (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {item?.color1 && <span title={item.color1} style={{ width: '14px', height: '14px', borderRadius: '50%', background: colorHex(item.color1) || C.lineSoft, border: '1px solid rgba(0,0,0,0.1)', display: 'inline-block' }} />}
                  {item?.color2 && <span title={item.color2} style={{ width: '14px', height: '14px', borderRadius: '50%', background: colorHex(item.color2) || C.lineSoft, border: '1px solid rgba(0,0,0,0.1)', display: 'inline-block' }} />}
                  {!item?.color1 && !item?.color2 && <span style={{ fontSize: '12px', color: C.inkMuted }}>—</span>}
                  {(item?.color1 || item?.color2) && (
                    <span style={{ fontSize: '11px', color: C.inkMuted }}>{[item.color1, item.color2].filter(Boolean).join('/')}</span>
                  )}
                </div>
              )
            },
            {
              label: '満足度',
              value: item?.satisfaction_score != null
                ? <span style={{ color: C.greenDark, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{(item.satisfaction_score * 100).toFixed(0)}%<span style={{ fontSize: '9px', fontWeight: 400, color: C.inkMuted, marginLeft: '3px' }}>{item.satisfaction_count}件</span></span>
                : '—'
            },
            {
              label: 'ベストワン',
              value: item?.best_one_ratio != null
                ? <span style={{ color: C.gold, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>★{(item.best_one_ratio * 100).toFixed(1)}%<span style={{ fontSize: '9px', fontWeight: 400, color: C.inkMuted, marginLeft: '3px' }}>{item.best_one_wins}/{item.best_one_total}</span></span>
                : '—'
            },
          ].map((attr, i) => (
            <div key={attr.label} style={{
              flex: 1, padding: '10px 14px',
              borderLeft: i === 0 ? 'none' : `1px solid ${C.lineSoft}`,
            }}>
              <div style={{ fontSize: '9px', color: C.inkMuted, letterSpacing: '0.06em', marginBottom: '4px' }}>{attr.label}</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.ink }}>{attr.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* グリッドレイアウト */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* 全体の中での位置づけ */}
        {card('全品目の中での位置づけ（全期間）', <PositioningCard positioning={positioning} C={C} />)}

        {/* 旬カレンダー */}
        {card(
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            旬カレンダー（1〜12月）
            <Link
              href={`/master/product_items?q=${encodeURIComponent(item?.name || '')}`}
              style={{ fontSize: '11px', fontWeight: 400, color: '#4a7c59', textDecoration: 'none' }}
            >
              品目マスタで編集 →
            </Link>
          </span>,
          <SeasonGridDisplay seasonByMonth={season_by_month} C={C} />
        )}

        {/* 月次トレンド */}
        {card('月次使用トレンド（棒の高さ＝回数、色＝お届けスコア）', <MonthlyTrendChart trend={monthly_trend} C={C} />)}

        {/* 2カラム */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* 登場周期 */}
          {card('登場周期分析（実際の間隔 vs 理想頻度）', <RhythmChart rhythm={appearance_rhythm} C={C} />)}

          {/* 評価軸スコア */}
          {card('評価軸別スコア（含むセットの平均）', <CriterionBars scores={criterion_scores} C={C} />)}
        </div>

        {/* 共起品目 */}
        {card('よく一緒に入る品目（列ヘッダで並び替え）', <CooccurrenceList items={cooccurring_items} C={C} />)}

        {/* セット一覧 */}
        {card(`使用セット一覧（直近${recent_sets?.length ?? 0}件）`, <SetsList sets={recent_sets} C={C} />)}
      </div>
    </AppLayout>
  )
}
